import cn from 'classnames'
import Avatar from 'ext-ui/components/Avatar/Avatar'
import { DropdownHandleProps } from 'ext-ui/components/Dropdown/hooks/useDropdownLogic'
import { TextInputProps } from 'ext-ui/components/TextInput/TextInput'
import React, { forwardRef } from 'react'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { Vendor } from 'src/queries/vendors/vendors.types'

import styles from './VendorSelectHandle.style'

export type VendorSelectHandleProps = DropdownHandleProps &
  Omit<TextInputProps, 'iconRight'> & {
    open: boolean
    selectedVendor: Vendor | null
    id?: string
    'aria-labelledby'?: string
  }

/**
 * Custom two-line trigger for the vendor picker. The standard
 * `SingleDropdownHandle` renders a single-line `TextInput` and has no slot for
 * the avatar + name/email identity block, so — exactly as
 * `CreditCardDropdownHandle` does — we build our own: a `role="button"` div
 * wired to the `open` / `onClick` / `onKeyDown` the hook hands us, showing the
 * selected vendor (or the placeholder) and a chevron.
 */
const VendorSelectHandle = forwardRef<HTMLDivElement, VendorSelectHandleProps>(
  (props, ref) => {
    const {
      open,
      selectedVendor,
      placeholder,
      className,
      id,
      onClick,
      onKeyDown,
      'aria-labelledby': ariaLabelledby,
    } = props

    return (
      <div
        ref={ref}
        id={id}
        className={cn('VendorSelectHandle', className, {
          selected: !!selectedVendor,
        })}
        css={styles}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role={'button'}
        tabIndex={0}
        aria-haspopup={'listbox'}
        aria-expanded={open}
        aria-labelledby={ariaLabelledby}
      >
        <div className={'content'}>
          {selectedVendor ? (
            <>
              <Avatar
                className={'avatar'}
                fallback={selectedVendor.name}
                size={28}
              />
              <span className={'identity'}>
                <span className={'name ellipsis'}>{selectedVendor.name}</span>
                <span className={'email ellipsis'}>{selectedVendor.email}</span>
              </span>
            </>
          ) : (
            <span className={'placeholder'}>{placeholder}</span>
          )}
        </div>
        <SvgChevronUp
          width={16}
          height={16}
          className={cn('chevron', { open })}
        />
      </div>
    )
  },
)

export default VendorSelectHandle
